import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FEATURES, LIMIT_DEFS } from '@goyone/shared';
import { money } from '@ui/lib/format';
import { Badge, ErrorState, Modal, PageHead } from '@ui/components/ui';
import { Icon } from '@ui/components/Icon';
import { api } from '../lib/api';

interface Plan {
  id: string;
  code: string;
  name: string;
  description: string;
  order: number;
  price: string;
  currency: string;
  cycle: 'MONTHLY' | 'YEARLY';
  isPublic: boolean;
  isActive: boolean;
  badge: string;
  features: string[];
  limits: Record<string, number>;
  tenantCount: number;
}

export function Plans() {
  const [editing, setEditing] = useState<Plan | null>(null);
  const queryClient = useQueryClient();

  const plans = useQuery({
    queryKey: ['plans'],
    queryFn: () => api<Plan[]>('/plans'),
  });

  return (
    <div className="page">
      <PageHead title="الباقات" subtitle="ما تشمله كل باقة وحدودها وسعرها" />

      {plans.isError && <ErrorState error={plans.error} onRetry={() => void plans.refetch()} />}
      {plans.isPending && <div className="skeleton" style={{ height: 260 }} />}

      <div className="stats">
        {plans.data?.map((p) => (
          <div key={p.id} className="card">
            <div className="card-head">
              <div className="col" style={{ gap: 2 }}>
                <div className="row" style={{ gap: 8 }}>
                  <h2 className="card-title">{p.name}</h2>
                  {p.badge && <Badge kind="info">{p.badge}</Badge>}
                  {!p.isActive && <Badge kind="neutral">موقوفة</Badge>}
                  {!p.isPublic && <Badge kind="warning">غير معروضة</Badge>}
                </div>
                <span className="tiny mute num">{p.code}</span>
              </div>
              <button
                className="btn btn-sm btn-icon btn-ghost"
                aria-label={`تعديل ${p.name}`}
                onClick={() => setEditing(p)}
              >
                <Icon name="edit" size={15} />
              </button>
            </div>

            <div className="card-body">
              <div className="stat-value num">{money(p.price)}</div>
              <div className="tiny mute">
                {p.cycle === 'YEARLY' ? 'سنويًا' : 'شهريًا'} — {p.tenantCount} مستأجرًا
              </div>

              <p className="small mute mt-2">{p.description}</p>

              <h4 className="section-title">الوحدات</h4>
              <div className="row" style={{ gap: 5, flexWrap: 'wrap' }}>
                {FEATURES.map((f) => (
                  <Badge key={f.key} kind={p.features.includes(f.key) ? 'success' : 'neutral'}>
                    {f.label}
                  </Badge>
                ))}
              </div>

              <h4 className="section-title">الحدود</h4>
              <div className="col" style={{ gap: 3 }}>
                {LIMIT_DEFS.map((l) => (
                  <div key={l.key} className="totals-row" style={{ padding: '2px 0' }}>
                    <span className="tiny mute">{l.label}</span>
                    <span className="tiny num">
                      {p.limits?.[l.key] === -1 ? 'بلا حد' : (p.limits?.[l.key] ?? '—')}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <PlanModal
          plan={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void queryClient.invalidateQueries({ queryKey: ['plans'] });
          }}
        />
      )}
    </div>
  );
}

function PlanModal({
  plan,
  onClose,
  onSaved,
}: {
  plan: Plan;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState({
    name: plan.name,
    description: plan.description,
    price: String(Number(plan.price)),
    cycle: plan.cycle,
    badge: plan.badge,
    isPublic: plan.isPublic,
    isActive: plan.isActive,
  });
  const [features, setFeatures] = useState<string[]>(plan.features ?? []);
  const [limits, setLimits] = useState<Record<string, string>>(() =>
    Object.fromEntries(LIMIT_DEFS.map((l) => [l.key, String(plan.limits?.[l.key] ?? '')])),
  );

  const set = (key: string, value: unknown) => setForm((f) => ({ ...f, [key]: value }));

  const save = useMutation({
    mutationFn: () =>
      api(`/plans/${plan.id}`, {
        method: 'PATCH',
        body: {
          name: form.name,
          description: form.description,
          price: Number(form.price) || 0,
          cycle: form.cycle,
          badge: form.badge,
          isPublic: form.isPublic,
          isActive: form.isActive,
          features,
          limits: Object.fromEntries(
            Object.entries(limits)
              .filter(([, v]) => v !== '')
              .map(([k, v]) => [k, Number(v)]),
          ),
        },
      }),
    onSuccess: onSaved,
  });

  const toggle = (key: string) =>
    setFeatures((f) => (f.includes(key) ? f.filter((x) => x !== key) : [...f, key]));

  return (
    <Modal
      title={`تعديل ${plan.name}`}
      onClose={onClose}
      wide
      footer={
        <>
          <button className="btn" onClick={onClose}>
            إلغاء
          </button>
          <button
            className="btn btn-primary"
            onClick={() => save.mutate()}
            disabled={save.isPending}
          >
            حفظ
          </button>
        </>
      }
    >
      {save.isError && (
        <div className="alert alert-danger mb-4">
          {save.error instanceof Error ? save.error.message : 'تعذّر الحفظ.'}
        </div>
      )}

      {plan.tenantCount > 0 && (
        <div className="alert alert-warning mb-4">
          هذه الباقة يشترك فيها {plan.tenantCount} مستأجرًا. تغيير وحداتها أو حدودها يسري عليهم
          فورًا. أما السعر فيسري على الاشتراكات الجديدة وحدها، لأن كل اشتراك يحفظ سعره المتفق
          عليه.
        </div>
      )}

      <div className="form-grid">
        <div className="field">
          <label className="label" htmlFor="pl-name">
            الاسم
          </label>
          <input
            id="pl-name"
            className="input"
            value={form.name}
            onChange={(e) => set('name', e.target.value)}
          />
        </div>

        <div className="field">
          <label className="label" htmlFor="pl-price">
            السعر
          </label>
          <input
            id="pl-price"
            className="input num"
            type="number"
            min="0"
            step="any"
            value={form.price}
            onChange={(e) => set('price', e.target.value)}
          />
        </div>

        <div className="field">
          <label className="label" htmlFor="pl-cycle">
            دورة الفوترة
          </label>
          <select
            id="pl-cycle"
            className="select"
            value={form.cycle}
            onChange={(e) => set('cycle', e.target.value)}
          >
            <option value="MONTHLY">شهرية</option>
            <option value="YEARLY">سنوية</option>
          </select>
        </div>

        <div className="field">
          <label className="label" htmlFor="pl-badge">
            شارة
          </label>
          <input
            id="pl-badge"
            className="input"
            value={form.badge}
            onChange={(e) => set('badge', e.target.value)}
            placeholder="الأكثر طلبًا"
          />
        </div>

        <div className="field span-2">
          <label className="label" htmlFor="pl-desc">
            الوصف
          </label>
          <textarea
            id="pl-desc"
            className="textarea"
            rows={2}
            value={form.description}
            onChange={(e) => set('description', e.target.value)}
          />
        </div>
      </div>

      <h4 className="section-title">الوحدات المشمولة</h4>
      <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
        {FEATURES.map((f) => (
          <label key={f.key} className="row pick-row" style={{ cursor: 'pointer', gap: 6 }}>
            <input
              type="checkbox"
              checked={features.includes(f.key)}
              onChange={() => toggle(f.key)}
            />
            <span className="small">{f.label}</span>
          </label>
        ))}
      </div>

      <h4 className="section-title">الحدود</h4>
      <div className="table-wrap">
        <table className="table">
          <thead>
            <tr>
              <th>الحد</th>
              <th style={{ width: 160 }}>القيمة</th>
            </tr>
          </thead>
          <tbody>
            {LIMIT_DEFS.map((l) => (
              <tr key={l.key}>
                <td className="cell-strong">{l.label}</td>
                <td>
                  <input
                    className="input num"
                    type="number"
                    min="-1"
                    aria-label={l.label}
                    value={limits[l.key] ?? ''}
                    onChange={(e) => setLimits((s) => ({ ...s, [l.key]: e.target.value }))}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <span className="tiny mute">القيمة ‎-1‎ تعني بلا حد.</span>

      <div className="col mt-4">
        <label className="row" style={{ cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={form.isPublic}
            onChange={(e) => set('isPublic', e.target.checked)}
          />
          <span className="small">معروضة في صفحة الأسعار</span>
        </label>
        <label className="row" style={{ cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={form.isActive}
            onChange={(e) => set('isActive', e.target.checked)}
          />
          <span className="small">متاحة للاشتراكات الجديدة</span>
        </label>
      </div>
    </Modal>
  );
}
